<template>
  <div id="app" class="app">
    <div class="wrapper">
      <div v-if="showInterface && isReady">
        <TheHeader />
        <main class="main">
          <div class="main__content">
            <TheTopbar />
            <transition
              name="custom-classes-transition"
              enter-active-class="animated fadeInUp delay-animation-700"
              leave-active-class="animated fadeOutDown">
              <router-view class="view" v-if="showView" />
            </transition>
          </div>
          <TheSidebar v-if="!isMobile" />
        </main>
        <TheFooter />
        <TheModalHelp v-if="showModalHelp" />
        <TheModalSendEmail v-if="showModalSendEmail" />
        <TheModalHolidayRequest v-if="showModalHolidayRequest" />
        <TheModalTravelRequest v-if="showModalTravelRequest" />
        <TheModalRemoteWorkingRequest v-if="showModalRemoteWorkingRequest" />
        <CookieBar v-if="!cookieConsent" @cookieconsent="getCookie"/>

      </div>
      <div v-if="!showInterface">
        <router-view class="view" />
      </div>
      <transition
        name="custom-classes-transition"
        enter-active-class="animated fadeIn"
        leave-active-class="animated fadeOut delay-animation-300">
        <TheLoader v-show="!loaded && showInterface && isReady" />
      </transition>
    </div>
  </div>
</template>
<script>

import { mapGetters, mapActions } from 'vuex'
import { getCookie } from '@modules/cookies'
import scrollMonitor from 'scrollmonitor'
import moment from 'moment'
import _ from 'lodash'
import { addListeners } from '@modules/event'
import TheHeader from '@blocks/header/TheHeader'
import TheFooter from '@blocks/footer/TheFooter'
import TheTopbar from '@blocks/topbar/TheTopbar'
import TheSidebar from '@blocks/sidebar/TheSidebar'

import TheLoader from '@blocks/loader/TheLoader'
import TheModalHelp from '@blocks/modals/TheModalHelp'
import TheModalSendEmail from '@blocks/modals/TheModalSendEmail'
import TheModalHolidayRequest from '@blocks/modals/TheModalHolidayRequest'
import TheModalTravelRequest from '@blocks/modals/TheModalTravelRequest'
import TheModalRemoteWorkingRequest from '@blocks/modals/TheModalRemoteWorkingRequest'
import CookieBar from '@blocks/cookie/CookieBar'

const breakpoints = [
  { name: 'small', min: 0, max: 767 },
  { name: 'medium', min: 768, max: 1023 },
  { name: 'large', min: 1024, max: 1440 },
  { name: 'xlarge', min: 1441, max: 20000 }
]

export default {
  data () {
    return {
      isLoaded: false,
      cookieConsent: null

    }
  },
  components: {
    TheHeader,
    TheSidebar,
    TheTopbar,
    TheFooter,
    TheLoader,
    TheModalHelp,
    TheModalSendEmail,
    TheModalHolidayRequest,
    TheModalTravelRequest,
    TheModalRemoteWorkingRequest,
    CookieBar
  },
  computed: {
    ...mapGetters({
      device: 'breakpoints/getDevice',
      breakpoint: 'breakpoints/getBreakpoint',
      modalName: 'modal/getName',
      isReady: 'common/getIsReady',
      isLoggedIn: 'auth/getIsLoggedIn',
      originalRoute: 'auth/getOriginalRoute',
      loaded: 'page/getLoaded'
    }),
    isMobile () {
      return this.device !== 'desktop'
    },
    showModalHelp () {
      return this.modalName === 'help'
    },
    showModalSendEmail () {
      return this.modalName === 'send-email'
    },
    showModalHolidayRequest () {
      return this.modalName === 'holiday'
    },
    showModalTravelRequest () {
      return this.modalName === 'travel'
    },
    showModalRemoteWorkingRequest () {
      return this.modalName === 'remoteWorking'
    },
    showView () {
      return this.$route.name === 'Login' || this.$route.name === 'New Password' || this.isReady
    },
    showInterface () {
      return this.$route.name !== 'Login' && this.$route.name !== 'New Password' && this.isLoggedIn
    }
  },
  watch: {
    isLoggedIn () {
      if (this.isLoggedIn) {
        this.fetchGetCommon().then(() => {
          this.updateIsReady(true)
          this.$router.replace(this.originalRoute)
        })
      }
    }
  },
  created () {
    this.getCookie()
  },
  mounted () {
    this.initialize()
    if (this.isLoggedIn) {
      this.fetchGetCommon().then(() => {
        this.updateIsReady(true)
      })
    }
    // this.requestAutoLogin().then(status => {
    //   // if (!status) this.$router.replace('/login')
    // })
  },
  methods: {
    ...mapActions({
      fetchGetCommon: 'common/fetchGetCommon',
      updateIsReady: 'common/updateIsReady',
      updateBreakpoints: 'breakpoints/updateBreakpoints',
      updateWindowWidth: 'breakpoints/updateWindowWidth',
      updateCalendarRange: 'events/updateCalendarRange',
      updateMonths: 'events/updateMonths',
      updateColors: 'events/updateColors',
      updateToken: 'auth/updateToken',
      requestAutoLogin: 'auth/requestAutoLogin'
    }),
    debouncedMethod: _.debounce(function () {
      this.handleWindowResize()
    }, 100),
    initialize () {
      this.updateBreakpoints(breakpoints)
      this.updateWindowWidth(window.innerWidth)
      this.updateEventsMonths()
      this.updateEventsColors()
      this.updateEventsCalendarRange()
      addListeners(window, 'resize', this.debouncedMethod)
    },
    handleWindowResize () {
      this.updateWindowWidth(window.innerWidth)
      this.updateScrollMonitor()
    },
    updateScrollMonitor () {
      scrollMonitor.recalculateLocations()
      scrollMonitor.update()
    },
    updateEventsMonths () {
      const monthsTranslations = _.cloneDeep(this.$t('message.months'))
      const monthName = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
      const months = {}
      monthName.forEach((name, index) => {
        months[name] = monthsTranslations[index].name
      })
      this.updateMonths(months)
    },
    updateEventsColors () {
      this.updateColors({
        'public-holidays': '#006DD9',
        'travels': '#FFC000',
        'remoteWorking': '#3a87ad',
        'holidays-absence': '#009ADF',
        'croplife-group-meeting': '#CC2993',
        'croplife-events': '#359A44',
        'external-events': '#FA9800',
        'meeting': '#F00E45',
        'homeworking': '#5A3C9B'
      })
    },
    updateEventsCalendarRange () {
      this.updateCalendarRange({
        min: 2004,
        max: moment().year() + 2
      })
    },
    getCookie () {
      this.cookieConsent = getCookie('cookie-consent')
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/main/main.scss";

.app{
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-color: colors("background");

  .main{
    display: flex;
    justify-content: flex-start;
    align-items: stretch;

    &__content{
      @media print {
        width: 100%;
      }
      @include media("small-medium"){
        width: 100%;
        flex: 0 1 auto;
      }
      @include media("large"){
        flex: 1;
        min-width: 0;
      }

      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;

      > *:not(.topbar){
        flex: 1 1 auto;
      }
    }

    .view{
      @include media("small-medium"){
        padding-top: layout("spacing");
        padding-bottom: layout("spacing");
      }
      @include media("large"){
        padding-top: layout("spacing")/2;
        padding-bottom: layout("spacing")/2;
      }
      width: 100%;
      flex: 1 1 auto;

    }

    .sidebar {
      flex: 0 0 auto;
    }
  }
}
</style>
